import React from 'react';
import { useHistory } from "react-router-dom";
import { withStyles, useTheme } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { TextInput } from '../../components/textInput';
import { withProfile } from "../profile";
import { mfa_styles } from './styles';
import Logo from '../../MenuSifu_New-Logo.png';
import USezpayLogo from '../../USezpay.svg';
import { Btn } from '../../components/global';
import { CircularProgress } from '@material-ui/core';
import { checkMFASession, debugCache, clearCognitoSession } from "../../APIs";
import { withMultiLanguagesConsumer } from "../../contexts/messages";

const MFAView = (props) => {
    const {
        classes,
        mfaFieldsInfo,
        mfaHandler,
        setInputField,
        loading,
        state,
        error,
        messagePool,
        resendHandler,
        resendLoading,
        isUSezpay
    } = props;
    const history = useHistory();
    console.log('MFA messagePool:', messagePool);
    console.log('MFA locale:', props.locale);
    const { 
        VERIFICATION_CODE, 
        VERIFY, 
        MFA_TITLE, 
        MFA_DESCRIPTION,
        VERIFICATION_CODE_PLACEHOLDER,
        VERIFICATION_CODE_ERROR,
        RESEND_CODE,
        BACK_TO_SIGN_IN
    } = messagePool || {};
    console.log('MFA extracted values:', { MFA_TITLE, MFA_DESCRIPTION, VERIFY, RESEND_CODE, BACK_TO_SIGN_IN });
    const theme = useTheme();
    const fullscreen = useMediaQuery(theme.breakpoints.down('sm'));

    const mfaFields = mfaFieldsInfo.map(field => {
        return (
            <div key={field.title} className={error ? classes.inputError : classes.input}>
                <TextInput 
                    inputProps={{'cy-test': 'verification-code'}}
                    label={VERIFICATION_CODE || "Verification Code"} 
                    helperMsg={field.helperMsg}
                    onChange={setInputField(field.field)}
                    refNode={field.node}
                    defaultValue={state[field.field]}
                    value={state[field.field]}
                    type="text"
                    placeholder={VERIFICATION_CODE_PLACEHOLDER || "Enter 6-digit code"}
                    maxLength={6}
                    onKeyDown={(e) => { 
                        if (e.keyCode === 13) {
                            mfaHandler();
                        }
                    }}
                />
            </div>
        )
    });

    // Add this function to handle "Back to Sign In" - force session loss
    const handleBackToSignIn = async () => {
        console.log("🔧 FRONTEND MFA - Back to Sign In: Clearing cognito session for fresh restart");

        try {
            // Clear everything for fresh login
            const response = await clearCognitoSession();
            console.log("🔧 FRONTEND MFA - All sessions cleared successfully. Sessions cleared:", response.data.sessionsCleared);
            console.log("🔧 FRONTEND MFA - Cognito cleared:", response.data.cognitoCleared);
            console.log("🔧 FRONTEND MFA - XSRF cleared:", response.data.xsrfCleared);
        } catch (error) {
            console.log("🔧 FRONTEND MFA - Error clearing sessions:", error);
        }

        // Clear frontend storage
        localStorage.removeItem('current_csrf_token');
        sessionStorage.removeItem('current_csrf_token');

        // Clear any other frontend cookies
        document.cookie = "cognito=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        document.cookie = "XSRF-TOKEN=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

        console.log("🔧 FRONTEND MFA - Back to Sign In: Redirecting to fresh login session");

        // Preserve the original redirect_uri and other OAuth parameters
        const urlParams = new URLSearchParams(window.location.search);
        const redirectUri = urlParams.get('redirect_uri');
        const codeChallenge = urlParams.get('code_challenge');
        const codeChallengeMethod = urlParams.get('code_challenge_method');

        // Build login URL with preserved parameters
        let loginUrl = '/login';
        const params = [];
        if (redirectUri) params.push(`redirect_uri=${encodeURIComponent(redirectUri)}`);
        if (codeChallenge) params.push(`code_challenge=${codeChallenge}`);
        if (codeChallengeMethod) params.push(`code_challenge_method=${codeChallengeMethod}`);

        if (params.length > 0) {
            loginUrl += '?' + params.join('&');
        }

        console.log("🔧 FRONTEND MFA - Redirecting to login with parameters:", loginUrl);

        // Redirect to login with fresh session and preserved parameters
        window.location.href = loginUrl;
    };

    // Add debug function
    const handleDebugSession = () => {
        // Check session status
        checkMFASession()
        .then(res => {
            console.log("🔧 FRONTEND MFA DEBUG - Session status:", res.data);
            alert(`Session Status: ${JSON.stringify(res.data, null, 2)}`);
        })
        .catch(err => {
            console.log("🔧 FRONTEND MFA DEBUG - Session check failed:", err);
            alert(`Session Check Failed: ${err.message}`);
        });
        
        // Debug cache
        debugCache()
        .then(res => {
            console.log("🔧 FRONTEND MFA DEBUG - Cache info:", res.data);
            alert(`Cache Info: ${JSON.stringify(res.data, null, 2)}`);
        })
        .catch(err => {
            console.log("🔧 FRONTEND MFA DEBUG - Cache debug failed:", err);
            alert(`Cache Debug Failed: ${err.message}`);
        });
    };

    // Determine branding based on isUSezpay flag
    const logoSrc = isUSezpay ? USezpayLogo : Logo;
    const logoAlt = isUSezpay ? "USezpay" : "MenuSifu";

    return (
        <div className={classes.root}>
            <Dialog
                fullScreen={fullscreen}
                open={true}
                className={classes.dialog}
                PaperProps={{
                    className: classes.paper
                }}
            >
                <div className={classes.container}>
                    <div className={classes.logoContainer}>
                        <img src={logoSrc} alt={logoAlt} className={classes.logo} />
                    </div>
                    
                    <div className={classes.formContainer}>
                        <h1 className={classes.title}>
                            {MFA_TITLE || "Two-Factor Authentication"}
                        </h1>
                        
                        <p className={classes.description}>
                            {MFA_DESCRIPTION || "We've sent a verification code to your phone number."}
                        </p>

                        <div className={classes.form}>
                            {error && (
                                <div className={classes.error}>
                                    {VERIFICATION_CODE_ERROR || "The code is incorrect, please try again"}
                                </div>
                            )}
                            
                            {mfaFields}
                            
                            <Btn
                                variant="contained"
                                color="primary"
                                fullWidth
                                onClick={mfaHandler}
                                disabled={loading}
                                className={classes.submitButton}
                                style={isUSezpay ? { backgroundColor: '#d9b97f', color: '#fff' } : {}}
                            >
                                {loading ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : (
                                    VERIFY || "Verify"
                                )}
                            </Btn>

                            <Btn
                                variant="outlined"
                                color="primary"
                                fullWidth
                                onClick={resendHandler}
                                disabled={resendLoading || loading}
                                className={classes.resendButton}
                                style={isUSezpay ? { marginTop: '10px', borderColor: '#d9b97f', color: '#d9b97f' } : { marginTop: '10px' }}
                            >
                                {resendLoading ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : (
                                    RESEND_CODE || "Resend Code"
                                )}
                            </Btn>
                        </div>

                        <div className={classes.backLink}>
                            <button 
                                type="button" 
                                onClick={handleBackToSignIn}
                                className={classes.backButton}
                            >
                                {BACK_TO_SIGN_IN || "Back to Sign In"}
                            </button>
                        </div>
                    </div>
                </div>
            </Dialog>
            {process.env.NODE_ENV === 'development' && (
                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                    <button 
                        type="button" 
                        onClick={handleDebugSession}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#f0f0f0',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                        }}
                    >
                        🔧 Debug Session
                    </button>
                </div>
            )}
        </div>
    );
};

export default withProfile(withMultiLanguagesConsumer(withStyles(mfa_styles)(MFAView))); 